// Word Menu Modal Component
// Standard navigation version (no SPA magic)

export function initWordMenuModal(): void {
  const modal = document.getElementById("word-menu-modal");
  if (!modal) return;

  const backdrop = modal.querySelector(".modal-backdrop") as HTMLElement;
  const closeBtn = modal.querySelector(".modal-close") as HTMLElement;
  const modalDialog = modal.querySelector(".modal-dialog") as HTMLElement;
  const modalWord = modal.querySelector(".modal-word") as HTMLElement;
  const modalTitle = modal.querySelector(".modal-title") as HTMLElement;
  const dictOption = modal.querySelector(".modal-option-dict") as HTMLAnchorElement;
  const anagramOption = modal.querySelector(".modal-option-anagram") as HTMLAnchorElement;
  const copyOption = modal.querySelector(".modal-option-copy") as HTMLButtonElement;

  let currentTrigger: HTMLElement | null = null;

  // --- Functions ---

  function positionModal(trigger: HTMLElement): void {
    if (window.innerWidth <= 768) return; // Mobile uses CSS centering

    const rect = trigger.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Center of the clicked word
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    modalDialog.style.left = centerX + "px";
    modalDialog.style.top = centerY + "px";
    modalDialog.style.transform = "translate(-50%, -50%)";

    // Edge guards
    const margin = 20;
    const modalWidth = 320;
    const modalHeight = 200;

    if (centerX - modalWidth / 2 < margin) {
      modalDialog.style.left = margin + modalWidth / 2 + "px";
    } else if (centerX + modalWidth / 2 > viewportWidth - margin) {
      modalDialog.style.left = viewportWidth - margin - modalWidth / 2 + "px";
    }

    if (centerY - modalHeight / 2 < margin) {
      modalDialog.style.top = margin + modalHeight / 2 + "px";
    } else if (centerY + modalHeight / 2 > viewportHeight - margin) {
      modalDialog.style.top = viewportHeight - margin - modalHeight / 2 + "px";
    }
  }

  function openModal(word: string, linksContainer: HTMLElement, trigger: HTMLElement): void {
    if (!linksContainer || linksContainer.children.length < 1) return;

    // Use class selectors to find links safely
    const dictLink = linksContainer.querySelector(".word-menu-link-dict") as HTMLAnchorElement;
    const anagramLink = linksContainer.querySelector(".word-menu-link-anagram") as HTMLAnchorElement;

    modalWord.textContent = word;

    // 1. Dictionary Option
    if (dictLink && dictLink.getAttribute("href") && dictLink.getAttribute("href") !== "#") {
      dictOption.href = dictLink.href;
      dictOption.style.display = "";
      const textSpan = dictOption.querySelector(".modal-option-text");
      if (textSpan) textSpan.textContent = dictLink.textContent;
    } else {
      dictOption.style.display = "none";
    }

    // 2. Anagram Option
    if (anagramLink && anagramLink.getAttribute("href") && anagramLink.getAttribute("href") !== "#") {
      anagramOption.href = anagramLink.href;
      anagramOption.style.display = "";
      const textSpan = anagramOption.querySelector(".modal-option-text");
      if (textSpan) textSpan.textContent = anagramLink.textContent;
    } else {
      anagramOption.style.display = "none";
    }

    // Position and Show
    currentTrigger = trigger;
    positionModal(trigger);
    
    modal!.style.display = ""; // Ensure it's not display:none from closing
    modal!.hidden = false;
    modal!.style.pointerEvents = "";
    
    closeBtn.focus();
  }

  function closeModal(): void {
    modal!.hidden = true;
    modal!.style.pointerEvents = "none";
    modal!.style.display = "none"; // Hide to reset animations
    
    // Reset positioning styles
    modalDialog.style.left = "";
    modalDialog.style.top = "";
    modalDialog.style.transform = "";
    
    currentTrigger = null;
  }

  // --- Event Listeners ---

  // Trigger click
  document.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    const trigger = target.closest("a.word-menu-trigger") as HTMLElement;

    if (trigger) {
      e.preventDefault(); // Stop default link navigation (it's just a trigger)
      
      const wrapper = trigger.closest(".word-menu-wrapper") as HTMLElement;
      if (wrapper) {
        const word = wrapper.dataset.word;
        const links = wrapper.querySelector(".word-menu-links") as HTMLElement;
        
        if (word && links) {
          openModal(word, links, trigger);
        }
      }
    }
  });

  // Close actions
  closeBtn.addEventListener("click", closeModal);
  backdrop.addEventListener("click", closeModal);
  modalTitle.addEventListener("click", closeModal);

  // Close when an option is clicked (before navigation)
  dictOption.addEventListener("click", closeModal);
  anagramOption.addEventListener("click", closeModal);

  // Copy to clipboard
  if (copyOption) {
    copyOption.addEventListener("click", () => {
      const word = modalWord.textContent;
      if (word) {
        navigator.clipboard.writeText(word);
      }
      closeModal();
    });
  }

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !modal.hidden) closeModal();
  });

  // Reposition on resize/scroll
  window.addEventListener("resize", () => {
    if (!modal.hidden && currentTrigger) positionModal(currentTrigger);
  });
  
  window.addEventListener("scroll", () => {
    if (!modal.hidden && currentTrigger) positionModal(currentTrigger);
  }, { passive: true });
}
